import { inject, Injectable, INJECTOR, isDevMode, signal } from '@angular/core';
import {
  AnalyticsEventTypes,
  ANALYTICS_CONSENT_STORAGE_KEY,
  AnalyticsConsent,
  AnalyticsEvent,
  AnalyticsEventType,
  analyticsConsentDecision,
  readStoredAnalyticsConsent,
} from '../entities';
import { APP_WINDOW } from '@bhindle/shared/core';
import { AnalyticsServiceInterface } from './analytics.service.interface';
import { FirebaseApp } from 'firebase/app';
import { ANALYTICS_FIREBASE_APP, FIREBASE_ANALYTICS } from '../tokens';

type LogEventFn = (
  analyticsInstance: { app: FirebaseApp },
  eventName: string,
  eventParams?: {
    [key: string]: unknown;
  },
  options?: unknown
) => void;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements AnalyticsServiceInterface {
  readonly #STORAGE_KEY = ANALYTICS_CONSENT_STORAGE_KEY;
  readonly #firebaseApp = inject(ANALYTICS_FIREBASE_APP, { optional: true });
  readonly #SESSION_ID = this.#generateSessionId();
  readonly #injector = inject(INJECTOR);
  readonly #consent = signal<AnalyticsConsent | null>(null);
  #analytics: { app: FirebaseApp } | null = null;
  #logEventFn: LogEventFn | null = null;
  readonly #pageStartTime = new Map<string, number>();
  readonly #routeStartTime = new Map<string, number>();
  readonly #appWindow: Window = inject(APP_WINDOW);
  readonly #appDocument = this.#appWindow.document;

  public consent = this.#consent.asReadonly();

  constructor() {
    this.#loadConsent();
  }

  public setConsent(consent: AnalyticsConsent): void {
    // Persist first, but never let storage failure block the decision.
    // `readStoredAnalyticsConsent` already guards the read because Safari with
    // cookies blocked throws on access rather than returning null. The write
    // path had no such guard, so for those users `setItem` threw before
    // `#consent.set`, the signal never moved, and the banner could not be
    // dismissed by accepting or declining — it returned on every single load,
    // on the exact arrival funnel this release exists to measure.
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(consent));
    } catch {
      // Storage unreachable. The choice still governs this session; it just
      // cannot outlive it, and they will be asked again next time.
    }
    this.#consent.set(consent);
    // Accepting has to start collection now. Bootstrap skipped initialisation
    // because there was no consent to read yet, so without this the first
    // session after accepting would record nothing. Declining has to drop the
    // instance: `#trackEvent` refuses without consent, but holding a live
    // Analytics object after someone said no leaves exactly one missed guard
    // between them and collection they declined.
    if (consent.isEnabled) void this.#attachAnalytics();
    else this.#analytics = null;
    this.#trackEvent({
      eventType: AnalyticsEventTypes.CONSENT_CHANGED,
      metadata: {
        enabled: consent.isEnabled,
        dataCollection: consent.dataCollection,
      },
    });
  }

  /**
   * Records a no. Delegates rather than clearing the key, because clearing it
   * is not the same thing: `readStoredAnalyticsConsent` returns null for an
   * absent key, `needsConsent()` reads null as "never asked", and the banner
   * comes straight back to nag someone who has already declined. A withdrawal
   * has to persist the false.
   */
  public revokeConsent(): void {
    this.setConsent(analyticsConsentDecision(false));
  }

  public trackPageView(page: string): void {
    const previousPage = Array.from(this.#pageStartTime.keys()).pop();
    if (previousPage) {
      const duration = Date.now() - this.#pageStartTime.get(previousPage)!;
      this.#trackTimeOnPage(previousPage, duration);
      this.#pageStartTime.delete(previousPage);
    }
    this.#pageStartTime.set(page, Date.now());

    this.#trackEvent({
      eventType: AnalyticsEventTypes.PAGE_VIEW,
      page,
      metadata: {
        url: this.#appWindow.location.pathname,
        referrer: this.#appDocument.referrer,
        title: this.#appDocument.title,
      },
    });
  }

  public trackRouteChange(to: string, params?: Record<string, string>): void {
    const previousRoute = Array.from(this.#routeStartTime.keys()).pop();
    if (previousRoute) {
      const duration = Date.now() - this.#routeStartTime.get(previousRoute)!;
      this.#trackTimeOnPage(previousRoute, duration);
      this.#routeStartTime.delete(previousRoute);
    }
    this.#routeStartTime.set(to, Date.now());

    this.#trackEvent({
      eventType: AnalyticsEventTypes.ROUTE_CHANGE,
      page: to,
      metadata: {
        from: previousRoute,
        to,
        params,
        url: this.#appWindow.location.pathname,
        referrer: this.#appDocument.referrer,
        title: this.#appDocument.title,
      },
    });
  }

  public trackSectionChange(to: string): void {
    this.#trackEvent({
      eventType: AnalyticsEventTypes.SECTION_CHANGE,
      metadata: {
        to,
      },
    });
  }

  public trackFeature(
    type: AnalyticsEventType,
    ...metadata: Record<string, unknown>[]
  ): void {
    this.#trackEvent({
      eventType: type,
      // Merge the rest args into one flat params object. The historical shape
      // index-keyed the array ({"0": {...}}), nesting the payload — which GA4
      // drops, so none of these params ever arrived.
      metadata: Object.assign({}, ...metadata),
    });
  }

  public trackSettingsChange(setting: string, value: unknown): void {
    this.#trackEvent({
      eventType: AnalyticsEventTypes.SETTINGS_CHANGED,
      metadata: {
        setting,
        value,
      },
    });
  }

  public trackError(error: Error, context?: Record<string, unknown>): void {
    this.#trackEvent({
      eventType: AnalyticsEventTypes.ERROR_OCCURRED,
      metadata: {
        message: error.message,
        stack: error.stack,
        context,
      },
    });
  }

  public trackPerformanceMetric(metric: string, value: number): void {
    this.#trackEvent({
      eventType: AnalyticsEventTypes.PERFORMANCE_METRIC,
      metadata: {
        metric,
        value,
      },
    });
  }

  #generateSessionId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  async #loadConsent() {
    // This used to read stored consent and then overwrite it with a hardcoded
    // all-true object, under a TODO saying it was "just for testing". The effect
    // in production was that a revocation survived exactly until the next reload
    // and analytics ran for everyone regardless of what they had chosen.
    this.#consent.set(readStoredAnalyticsConsent());

    if (!this.#consent()?.isEnabled) return;
    await this.#attachAnalytics();
  }

  /**
   * Binds the Analytics instance and `logEvent`. Safe to call more than once —
   * it returns early once attached, so the boot path and a later acceptance
   * cannot double-initialise.
   *
   * Prefers the instance the provider created at bootstrap and falls back to
   * building one from {@link ANALYTICS_FIREBASE_APP}. The fallback is the
   * accept-mid-session path: at bootstrap there was no consent, so the provider
   * deliberately registered no `FIREBASE_ANALYTICS` for the injector to hand out.
   */
  async #attachAnalytics(): Promise<void> {
    if (this.#analytics && this.#logEventFn) return;

    try {
      const { getAnalytics, isSupported, logEvent } = await import('firebase/analytics');
      if (!logEvent || !(await isSupported())) {
        console.warn('Analytics service is not available');
        return;
      }

      this.#logEventFn = logEvent as unknown as LogEventFn;
      this.#analytics =
        this.#injector.get(FIREBASE_ANALYTICS, null) ??
        (this.#firebaseApp ? getAnalytics(this.#firebaseApp) : null);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  }

  #trackTimeOnPage(page: string, duration: number): void {
    this.#trackEvent({
      eventType: AnalyticsEventTypes.TIME_ON_PAGE,
      page,
      metadata: {
        duration,
        timestamp: new Date().toISOString(),
      },
    });
  }

  #trackEvent(event: Omit<AnalyticsEvent, 'timestamp' | 'sessionId'>): void {
    if (!this.#analytics || !this.#logEventFn) {
      return;
    }
    if (!this.#consent()?.isEnabled || isDevMode()) return;
    const analyticsEvent: AnalyticsEvent = {
      ...event,
      timestamp: new Date(),
      sessionId: this.#SESSION_ID,
    };

    const anonymizedEvent = this.#anonymizeEvent(analyticsEvent);

    this.#logEventFn(this.#analytics, event.eventType, {
      ...anonymizedEvent,
      sessionId: anonymizedEvent.sessionId,
      timestamp: anonymizedEvent.timestamp.toISOString(),
    });
  }

  #anonymizeEvent(event: AnalyticsEvent): AnalyticsEvent {
    const { metadata, ...rest } = event;
    return {
      ...rest,
      metadata: this.#anonymizeMetadata(metadata),
    };
  }

  #anonymizeMetadata(
    metadata: Record<string, unknown> | undefined
  ): Record<string, unknown> {
    const sensitiveKeys = ['userId', 'uid', 'email', 'name'];
    const anonymizedEvent: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(metadata ?? {})) {
      if (sensitiveKeys.includes(key)) {
        anonymizedEvent[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        anonymizedEvent[key] = this.#anonymizeMetadata(
          value as Record<string, unknown>
        );
      } else {
        anonymizedEvent[key] = value;
      }
    }
    return anonymizedEvent;
  }
}
